import { LineChart } from '@mui/x-charts';
import { convertDecimalToAmerican, getRandomColor, minutesSince } from '../utils';
import { LogoGenerator } from '../../../components/LogoGenerator';
import './OddsHistoryGraph.css';
import { sportsbookColourMapping } from './SportsbookColourMapping';
import { getSportsbookDisplayName } from '../../../utils/sportsbookAliases';
import { generateTickValues } from './generateTickValues';
import { TimeRangeSelector } from './TimeRangeSelector';
import { linksPJ } from '../EVScreen/sampleData';

export const OddsHistoryGraph = (props) => {
	const pageWidth = document.documentElement.clientWidth;
	// console.log('pageWidth: ', pageWidth);
	let {
		isMobile,
		setIsModalOpen = () => {}, // Default empty function
		isModalOpen = false, // Default false
		oddsHistories,
		split,
		sportsbooksToHighlight,
		// setAdditionalSportsbooksToHighlight,
		// additionalSportsbooksToHighlight,
		sharpFilter,
		sharpBook,
		timeRange,
		selected,
		setSelected,
		overSide,
		underSide,
		tutorial,
		selectedSide = 'over', // Default to over
		points, // Raw points value for Point Spread markets
		height = 300, // Default height, can be overridden
	} = props;

	if (tutorial) {
		oddsHistories = linksPJ;
	}

const normalizeTeamName = (name) => {
	if (!name) return '';
	return name.toString().toLowerCase().replace(/[^a-z0-9]/g, '');
};

	const xAxisValues = [];
	const yAxisValues = [];
	const xAxisValuesUnder = [];
	const yAxisValuesUnder = [];
	// Track team labels per series to distinguish both sides on spreads
	const seriesTeamLabels = [];
	const seriesTeamLabelsUnder = [];

	if (oddsHistories?.length) {
		console.log('OddsHistoryGraph: Processing', oddsHistories.length, 'odds histories');
		console.log('OddsHistoryGraph: overSide:', overSide, 'underSide:', underSide);
		
		
		// Pre-process odds histories to handle WagerWiz combination before sharpFilter logic
		const processedOddsHistories = [];
		const wagerWizEntries = [];
		
		// Check if sharpFilter indicates this is a WagerWiz source
		const isWagerWizSharpFilter = sharpFilter?.includes?.('WagerWiz Props') || 
			sharpFilter?.includes?.('WagerWiz Mains') || 
			sharpFilter?.includes?.('WagerWiz') ||
			sharpFilter === 'WagerWiz Props' ||
			sharpFilter === 'WagerWiz Mains' ||
			sharpFilter === 'WagerWiz';
		
		for (const oh of oddsHistories) {
			// Store original sportsbook name for processing purposes (don't modify original data)
			// Only set originalSportsbook if it doesn't already exist
			if (!oh.originalSportsbook) {
				oh.originalSportsbook = oh.sportsbook;
			}
			const originalSportsbook = oh.originalSportsbook;
			
			// Collect WagerWiz entries for later combination
			// Check both originalSportsbook and current sportsbook name
			// Also treat sharp book entries as WagerWiz if sharpFilter indicates WagerWiz
			const isSharpBookEntry = sharpBook?.includes?.(originalSportsbook) || sharpBook === originalSportsbook;
			const isWagerWizSource = isSharpBookEntry && isWagerWizSharpFilter;
			
			if (originalSportsbook === 'WagerWiz Props' || originalSportsbook === 'WagerWiz Mains' ||
				oh.sportsbook === 'WagerWiz Props' || oh.sportsbook === 'WagerWiz Mains' ||
				(oh.sportsbook && oh.sportsbook.includes('WagerWiz Props')) ||
				(oh.sportsbook && oh.sportsbook.includes('WagerWiz Mains')) ||
				oh.sportsbook === 'WagerWiz' || originalSportsbook === 'WagerWiz' ||
				isWagerWizSource) {
				// Mark this entry as WagerWiz for display purposes
				if (isWagerWizSource && originalSportsbook !== 'WagerWiz' && 
					originalSportsbook !== 'WagerWiz Props' && originalSportsbook !== 'WagerWiz Mains') {
					oh.sportsbook = 'WagerWiz';
					oh.colour = '#0042a4';
				}
				wagerWizEntries.push(oh);
			} else {
				processedOddsHistories.push(oh);
			}
		}
		
		// Also check for entries that might have been processed by sharpFilter logic
		// Look for entries with combined names like "WagerWiz Props, WagerWiz Mains"
		const combinedWagerWizEntries = oddsHistories.filter(oh => 
			oh.sportsbook && oh.sportsbook.includes('WagerWiz Props') && oh.sportsbook.includes('WagerWiz Mains')
		);
		
		// Also check for multiple entries that are all labeled as "WagerWiz"
		const multipleWagerWizEntries = oddsHistories.filter(oh => 
			oh.sportsbook === 'WagerWiz' || oh.originalSportsbook === 'WagerWiz'
		);
		
		
		// Handle multiple WagerWiz entries that are all labeled as "WagerWiz"
		if (multipleWagerWizEntries.length >= 2) {
			// Merge multiple entries that are all labeled as "WagerWiz"
			const mergedEntry = {
				...multipleWagerWizEntries[0],
				sportsbook: 'WagerWiz',
				originalSportsbook: 'WagerWiz',
				colour: '#0042a4'
			};
			
			// Combine history data by averaging prices at each time point
			const combineHistoryData = (histories) => {
				if (!histories || histories.length === 0) return [];
				
				// Create a map of time -> prices for all histories
				const timePriceMap = new Map();
				
				histories.forEach(history => {
					if (history && Array.isArray(history)) {
						history.forEach(item => {
							if (item.time || item.timestamp) {
								const timestamp = new Date(item.time || item.timestamp).getTime();
								if (!isNaN(timestamp)) {
									if (!timePriceMap.has(timestamp)) {
										timePriceMap.set(timestamp, []);
									}
									timePriceMap.get(timestamp).push(item.price);
								}
							}
						});
					}
				});
				
				// Create combined data with averaged prices
				const combinedData = [];
				timePriceMap.forEach((prices, timestamp) => {
					const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
					combinedData.push({
						time: new Date(timestamp).toISOString(),
						price: averagePrice
					});
				});
				
				// Sort by time
				combinedData.sort((a, b) => new Date(a.time) - new Date(b.time));
				return combinedData;
			};
			
			// Combine all history types from all entries
			const allOverHistories = multipleWagerWizEntries.map(entry => entry.bet_price_over_history).filter(Boolean);
			const allUnderHistories = multipleWagerWizEntries.map(entry => entry.bet_price_under_history).filter(Boolean);
			const allOverPositiveHistories = multipleWagerWizEntries.map(entry => entry.bet_price_history_over_positive).filter(Boolean);
			const allUnderPositiveHistories = multipleWagerWizEntries.map(entry => entry.bet_price_history_under_positive).filter(Boolean);
			const allOverNegativeHistories = multipleWagerWizEntries.map(entry => entry.bet_price_history_over_negative).filter(Boolean);
			const allUnderNegativeHistories = multipleWagerWizEntries.map(entry => entry.bet_price_history_under_negative).filter(Boolean);
			
			mergedEntry.bet_price_over_history = combineHistoryData(allOverHistories);
			mergedEntry.bet_price_under_history = combineHistoryData(allUnderHistories);
			mergedEntry.bet_price_history_over_positive = combineHistoryData(allOverPositiveHistories);
			mergedEntry.bet_price_history_under_positive = combineHistoryData(allUnderPositiveHistories);
			mergedEntry.bet_price_history_over_negative = combineHistoryData(allOverNegativeHistories);
			mergedEntry.bet_price_history_under_negative = combineHistoryData(allUnderNegativeHistories);
			
			processedOddsHistories.push(mergedEntry);
			
			// Remove the multiple WagerWiz entries from the main processing
			oddsHistories = oddsHistories.filter(oh => 
				!(oh.sportsbook === 'WagerWiz' || oh.originalSportsbook === 'WagerWiz')
			);
		}
		
		// Handle combined WagerWiz entries that might have been processed by sharpFilter
		if (combinedWagerWizEntries.length >= 2) {
			// Merge multiple entries with the same combined name
			const mergedEntry = {
				...combinedWagerWizEntries[0],
				sportsbook: 'WagerWiz',
				originalSportsbook: 'WagerWiz',
				colour: '#0042a4'
			};
			
			// Combine history data by averaging prices at each time point
			const combineHistoryData = (histories) => {
				if (!histories || histories.length === 0) return [];
				
				// Create a map of time -> prices for all histories
				const timePriceMap = new Map();
				
				histories.forEach(history => {
					if (history && Array.isArray(history)) {
						history.forEach(item => {
							if (item.time || item.timestamp) {
								const timestamp = new Date(item.time || item.timestamp).getTime();
								if (!isNaN(timestamp)) {
									if (!timePriceMap.has(timestamp)) {
										timePriceMap.set(timestamp, []);
									}
									timePriceMap.get(timestamp).push(item.price);
								}
							}
						});
					}
				});
				
				// Create combined data with averaged prices
				const combinedData = [];
				timePriceMap.forEach((prices, timestamp) => {
					const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
					combinedData.push({
						time: new Date(timestamp).toISOString(),
						price: averagePrice
					});
				});
				
				// Sort by time
				combinedData.sort((a, b) => new Date(a.time) - new Date(b.time));
				return combinedData;
			};
			
			// Combine all history types from all entries
			const allOverHistories = combinedWagerWizEntries.map(entry => entry.bet_price_over_history).filter(Boolean);
			const allUnderHistories = combinedWagerWizEntries.map(entry => entry.bet_price_under_history).filter(Boolean);
			const allOverPositiveHistories = combinedWagerWizEntries.map(entry => entry.bet_price_history_over_positive).filter(Boolean);
			const allUnderPositiveHistories = combinedWagerWizEntries.map(entry => entry.bet_price_history_under_positive).filter(Boolean);
			const allOverNegativeHistories = combinedWagerWizEntries.map(entry => entry.bet_price_history_over_negative).filter(Boolean);
			const allUnderNegativeHistories = combinedWagerWizEntries.map(entry => entry.bet_price_history_under_negative).filter(Boolean);
			
			mergedEntry.bet_price_over_history = combineHistoryData(allOverHistories);
			mergedEntry.bet_price_under_history = combineHistoryData(allUnderHistories);
			mergedEntry.bet_price_history_over_positive = combineHistoryData(allOverPositiveHistories);
			mergedEntry.bet_price_history_under_positive = combineHistoryData(allUnderPositiveHistories);
			mergedEntry.bet_price_history_over_negative = combineHistoryData(allOverNegativeHistories);
			mergedEntry.bet_price_history_under_negative = combineHistoryData(allUnderNegativeHistories);
			
			processedOddsHistories.push(mergedEntry);
			
			// Remove the combined entries from the main processing
			oddsHistories = oddsHistories.filter(oh => 
				!(oh.sportsbook && oh.sportsbook.includes('WagerWiz Props') && oh.sportsbook.includes('WagerWiz Mains'))
			);
		}
		
		// Combine WagerWiz entries if we have both Props and Mains
		if (wagerWizEntries.length >= 2) {
			const wagerWizProps = wagerWizEntries.find(oh => 
				oh.originalSportsbook === 'WagerWiz Props' || 
				oh.sportsbook === 'WagerWiz Props' ||
				(oh.sportsbook && oh.sportsbook.includes('WagerWiz Props'))
			);
			const wagerWizMains = wagerWizEntries.find(oh => 
				oh.originalSportsbook === 'WagerWiz Mains' || 
				oh.sportsbook === 'WagerWiz Mains' ||
				(oh.sportsbook && oh.sportsbook.includes('WagerWiz Mains'))
			);
			
			if (wagerWizProps && wagerWizMains) {
				// Create a combined entry
				const combinedEntry = {
					...wagerWizProps,
					sportsbook: 'WagerWiz',
					originalSportsbook: 'WagerWiz',
					colour: '#0042a4'
				};
				
				// Combine history data by averaging prices at each time point
				const combineHistoryData = (propsHistory, mainsHistory) => {
					if (!propsHistory || !mainsHistory) return propsHistory || mainsHistory || [];
					
					// Create a map of time -> price for both histories
					const propsMap = new Map();
					const mainsMap = new Map();
					
					propsHistory.forEach(item => {
						if (item.time || item.timestamp) {
							const timestamp = new Date(item.time || item.timestamp).getTime();
							if (!isNaN(timestamp)) {
								propsMap.set(timestamp, item.price);
							}
						}
					});
					
					mainsHistory.forEach(item => {
						if (item.time || item.timestamp) {
							const timestamp = new Date(item.time || item.timestamp).getTime();
							if (!isNaN(timestamp)) {
								mainsMap.set(timestamp, item.price);
							}
						}
					});
					
					// Get all unique timestamps
					const allTimestamps = new Set([...propsMap.keys(), ...mainsMap.keys()]);
					
					// Create combined data with averaged prices
					const combinedData = [];
					allTimestamps.forEach(timestamp => {
						const propsPrice = propsMap.get(timestamp);
						const mainsPrice = mainsMap.get(timestamp);
						
						if (propsPrice !== undefined && mainsPrice !== undefined) {
							// Average the prices
							combinedData.push({
								time: new Date(timestamp).toISOString(),
								price: (propsPrice + mainsPrice) / 2
							});
						} else if (propsPrice !== undefined) {
							// Only props data available
							combinedData.push({
								time: new Date(timestamp).toISOString(),
								price: propsPrice
							});
						} else if (mainsPrice !== undefined) {
							// Only mains data available
							combinedData.push({
								time: new Date(timestamp).toISOString(),
								price: mainsPrice
							});
						}
					});
					
					// Sort by time
					combinedData.sort((a, b) => new Date(a.time) - new Date(b.time));
					return combinedData;
				};
				
				// Combine all history types
				combinedEntry.bet_price_over_history = combineHistoryData(
					wagerWizProps.bet_price_over_history, 
					wagerWizMains.bet_price_over_history
				);
				combinedEntry.bet_price_under_history = combineHistoryData(
					wagerWizProps.bet_price_under_history, 
					wagerWizMains.bet_price_under_history
				);
				combinedEntry.bet_price_history_over_positive = combineHistoryData(
					wagerWizProps.bet_price_history_over_positive, 
					wagerWizMains.bet_price_history_over_positive
				);
				combinedEntry.bet_price_history_under_positive = combineHistoryData(
					wagerWizProps.bet_price_history_under_positive, 
					wagerWizMains.bet_price_history_under_positive
				);
				combinedEntry.bet_price_history_over_negative = combineHistoryData(
					wagerWizProps.bet_price_history_over_negative, 
					wagerWizMains.bet_price_history_over_negative
				);
				combinedEntry.bet_price_history_under_negative = combineHistoryData(
					wagerWizProps.bet_price_history_under_negative, 
					wagerWizMains.bet_price_history_under_negative
				);
				
				processedOddsHistories.push(combinedEntry);
			} else {
				// No Props/Mains combination found, add all WagerWiz entries directly
				// This handles cases like DraftKings being a WagerWiz source
				for (const entry of wagerWizEntries) {
					entry.sportsbook = 'WagerWiz';
					entry.colour = '#0042a4';
					processedOddsHistories.push(entry);
				}
			}
		} else if (wagerWizEntries.length === 1) {
			// If only one WagerWiz entry, just rename it
			const singleEntry = wagerWizEntries[0];
			singleEntry.sportsbook = 'WagerWiz';
			singleEntry.originalSportsbook = 'WagerWiz';
			singleEntry.colour = '#0042a4';
			processedOddsHistories.push(singleEntry);
		}
		
		// Now apply sharpFilter logic to the processed data
		for (const oh of processedOddsHistories) {
			const originalSportsbook = oh.originalSportsbook;
			
			
			if (sharpBook?.includes(originalSportsbook) && (sharpFilter.includes('WagerWiz Props') || sharpFilter.includes('WagerWiz Mains') || sharpFilter.includes('WagerWiz'))) {
				// For WagerWiz entries, keep them as "WagerWiz" instead of using sharpFilter.join(', ')
				if (originalSportsbook === 'WagerWiz') {
					oh.sportsbook = 'WagerWiz';
				} else {
				oh.sportsbook = sharpFilter.join(', ');
				}
				
			}
			
			// Always assign color based on original sportsbook name
			// Special case for WagerWiz to always use the specific color
			if (originalSportsbook === 'WagerWiz') {
				oh.colour = '#0042a4';
			} else {
				oh.colour = sportsbookColourMapping[originalSportsbook] || getRandomColor();
			}
			
		}
		
		// After sharpFilter processing, check if we have multiple entries with the same name and combine them
		// For Point Spread histories, group by sportsbook AND team so both sides render separately
		const finalProcessedOddsHistories = [];
		const nameGroups = {};
		
		// Helper to normalize WagerWiz-related names to just "WagerWiz"
		const normalizeWagerWizName = (name) => {
			if (!name) return name;
			if (name === 'WagerWiz' || name === 'WagerWiz Props' || name === 'WagerWiz Mains' ||
				name.includes('WagerWiz Props') || name.includes('WagerWiz Mains') || name.includes('WagerWiz')) {
				return 'WagerWiz';
			}
			return name;
		};
		
		// Group entries by key: sportsbook or sportsbook::team for spreads
		// Normalize WagerWiz-related names to combine all WagerWiz entries
		for (const oh of processedOddsHistories) {
			let sportsbookName = getSportsbookDisplayName(normalizeWagerWizName(oh.sportsbook));
			// Also normalize the sportsbook on the entry itself
			if (sportsbookName === 'WagerWiz') {
				oh.sportsbook = 'WagerWiz';
				oh.colour = '#0042a4';
			}
			let finalName = sportsbookName;
			try {
				const parts = (oh.outcome_id_normalized || '').split(':');
				if (parts[1] === 'Point Spread' || parts[1] === 'Asian Handicap') {
					const team = parts[2] || '';
					finalName = `${sportsbookName}::${team}`;
				}
			} catch (e) {}
			if (!nameGroups[finalName]) {
				nameGroups[finalName] = [];
			}
			nameGroups[finalName].push(oh);
		}
		
		// Process each group
		for (const [name, entries] of Object.entries(nameGroups)) {
			// Get the base sportsbook name (without team suffix)
			const baseName = name.includes('::') ? name.split('::')[0] : name;
			const isWagerWiz = baseName === 'WagerWiz' || baseName.includes('WagerWiz');
			
			if (entries.length > 1) {
				// Combine multiple entries with the same name
				const combinedEntry = {
					...entries[0],
					// Keep sportsbook label without team suffix for display, stash team separately
					sportsbook: isWagerWiz ? 'WagerWiz' : baseName,
					teamLabel: (name.includes('::') ? name.split('::')[1] : ''),
					originalSportsbook: isWagerWiz ? 'WagerWiz' : baseName,
					colour: isWagerWiz ? '#0042a4' : (sportsbookColourMapping[baseName] || getRandomColor())
				};
				
				// Combine history data by averaging prices at each time point
				const combineHistoryData = (histories) => {
					if (!histories || histories.length === 0) return [];
					
					// Create a map of time -> prices for all histories
					const timePriceMap = new Map();
					
					histories.forEach(history => {
						if (history && Array.isArray(history)) {
							history.forEach(item => {
								if (item.time || item.timestamp) {
									const timestamp = new Date(item.time || item.timestamp).getTime();
									if (!isNaN(timestamp)) {
										if (!timePriceMap.has(timestamp)) {
											timePriceMap.set(timestamp, []);
										}
										timePriceMap.get(timestamp).push(item.price);
									}
								}
							});
						}
					});
					
					// Create combined data with averaged prices
					const combinedData = [];
					timePriceMap.forEach((prices, timestamp) => {
						const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
						combinedData.push({
							time: new Date(timestamp).toISOString(),
							price: averagePrice
						});
					});
					
					// Sort by time
					combinedData.sort((a, b) => new Date(a.time) - new Date(b.time));
					return combinedData;
				};
				
				// Combine all history types from all entries
				const allOverHistories = entries.map(entry => entry.bet_price_over_history).filter(Boolean);
				const allUnderHistories = entries.map(entry => entry.bet_price_under_history).filter(Boolean);
				const allOverPositiveHistories = entries.map(entry => entry.bet_price_history_over_positive).filter(Boolean);
				const allUnderPositiveHistories = entries.map(entry => entry.bet_price_history_under_positive).filter(Boolean);
				const allOverNegativeHistories = entries.map(entry => entry.bet_price_history_over_negative).filter(Boolean);
				const allUnderNegativeHistories = entries.map(entry => entry.bet_price_history_under_negative).filter(Boolean);
				
				combinedEntry.bet_price_over_history = combineHistoryData(allOverHistories);
				combinedEntry.bet_price_under_history = combineHistoryData(allUnderHistories);
				combinedEntry.bet_price_history_over_positive = combineHistoryData(allOverPositiveHistories);
				combinedEntry.bet_price_history_under_positive = combineHistoryData(allUnderPositiveHistories);
				combinedEntry.bet_price_history_over_negative = combineHistoryData(allOverNegativeHistories);
				combinedEntry.bet_price_history_under_negative = combineHistoryData(allUnderNegativeHistories);
				
				finalProcessedOddsHistories.push(combinedEntry);
			} else {
				const single = { ...entries[0] };
				// Normalize WagerWiz names for single entries too
				if (isWagerWiz) {
					single.sportsbook = 'WagerWiz';
					single.originalSportsbook = 'WagerWiz';
					single.colour = '#0042a4';
				}
				// Normalize label fields similarly for single entries
				try {
					const parts = (single.outcome_id_normalized || '').split(':');
					if (parts[1] === 'Point Spread' || parts[1] === 'Asian Handicap') {
						single.teamLabel = parts[2] || '';
					}
				} catch (e) {}
				finalProcessedOddsHistories.push(single);
			}
		}
		
		// Use the final processed odds histories
		let finalOddsHistories = finalProcessedOddsHistories;
		const isSpreadMarket = split && (split[1] === 'Point Spread' || split[1] === 'Asian Handicap');
		
		// Calculate targetTeam for spread markets (needed for both filtering and data lookup)
		let targetTeamForLookup = '';
		if (isSpreadMarket) {
			const selectedTeamFromSplit = normalizeTeamName(split?.[2]);
			
			// Collect every distinct team present in the fetched histories
			const teamsInHistories = Array.from(
				new Set(
					finalProcessedOddsHistories
						.map((oh) => {
							try {
								const parts = (oh.outcome_id_normalized || '').split(':');
								if (parts[1] === 'Point Spread' || parts[1] === 'Asian Handicap') {
									return normalizeTeamName(parts[2]);
								}
							} catch (e) {}
							return null;
						})
						.filter(Boolean)
				)
			);
			
			const otherTeam = teamsInHistories.find((team) => team && team !== selectedTeamFromSplit) || '';
			const targetTeam = selectedSide === 'under'
				? (otherTeam || selectedTeamFromSplit || teamsInHistories[0] || '')
				: (selectedTeamFromSplit || teamsInHistories[0] || '');
			
			targetTeamForLookup = targetTeam;
			
			// DEBUG: Log targetTeam calculation
			if (selectedSide === 'under') {
				console.log('[UNDER DEBUG] Target team calculation:', {
					split,
					selectedTeamFromSplit,
					teamsInHistories,
					otherTeam,
					targetTeam,
					selectedSide
				});
			}
			
			finalOddsHistories = finalProcessedOddsHistories.filter((oh) => {
				// For spread markets, filter by team for ALL entries including WagerWiz
				try {
					const parts = (oh.outcome_id_normalized || '').split(':');
					if (parts[1] === 'Point Spread' || parts[1] === 'Asian Handicap') {
						const entryTeam = normalizeTeamName(parts[2]);
						if (!targetTeam) {
							return true;
						}
						return entryTeam === targetTeam;
					}
					return true;
				} catch (e) {
					return true;
				}
			});
		}
		
		console.log('OddsHistoryGraph: Final odds histories:', finalOddsHistories.map(oh => ({ sportsbook: oh.sportsbook, hasOverHistory: !!oh.bet_price_over_history, hasUnderHistory: !!oh.bet_price_under_history })));

		// Helper function to smooth price data using time-weighted moving average
		// This is specifically for WagerWiz to reduce spikiness from aggregated data
		// Uses a time window approach to handle data points with different timestamps
		const smoothPriceData = (xValues, yValues, timeWindowMinutes = 10) => {
			if (!xValues || !yValues || xValues.length !== yValues.length || xValues.length < 2) {
				return { xValues, yValues };
			}
			
			const smoothedX = [];
			const smoothedY = [];
			
			// Apply time-weighted moving average to smooth the prices
			for (let i = 0; i < yValues.length; i++) {
				const currentTime = xValues[i];
				let weightedSum = 0;
				let totalWeight = 0;
				
				// Look at all points within the time window
				for (let j = 0; j < yValues.length; j++) {
					const timeDiff = Math.abs(xValues[j] - currentTime);
					
					// Only include points within the time window
					if (timeDiff <= timeWindowMinutes) {
						// Weight inversely proportional to time distance (closer points have more weight)
						// Use a Gaussian-like weighting function
						const weight = Math.exp(-(timeDiff * timeDiff) / (2 * (timeWindowMinutes / 3) * (timeWindowMinutes / 3)));
						weightedSum += yValues[j].price * weight;
						totalWeight += weight;
					}
				}
				
				// Calculate weighted average
				const smoothedPrice = totalWeight > 0 ? weightedSum / totalWeight : yValues[i].price;
				smoothedX.push(xValues[i]);
				smoothedY.push({ ...yValues[i], price: smoothedPrice });
			}
			
			return { xValues: smoothedX, yValues: smoothedY };
		};

		// for (const oh of oddsHistories) {
		// 	if (oh.sportsbook.includes('WagerWiz')) {
		// 		oh.colour = '#2555B3';
		// 	}
		// }


		// Helper function to find history data based on outcome_id_normalized
		// The backend may store data in the wrong field, so we check all fields and use whichever has data
		// as long as the outcome_id_normalized matches the expected team and points sign
		// CRITICAL: Prioritize fields that match the expected points sign
		const findHistoryDataByOutcomeId = (oddsHistory, expectedTeam, expectedPoints) => {
			if (!oddsHistory.outcome_id_normalized) return null;
			
			const parts = oddsHistory.outcome_id_normalized.split(':');
			if (parts.length < 4 || (parts[1] !== 'Point Spread' && parts[1] !== 'Asian Handicap')) {
				return null;
			}
			
			const outcomeTeam = parts[2];
			const outcomePoints = parts[3];
			
			// Check if this outcome matches the expected team
			if (normalizeTeamName(outcomeTeam) !== normalizeTeamName(expectedTeam)) {
				return null;
			}
			
			// If expectedPoints is provided, also check that the points match (normalize by removing leading '+')
			if (expectedPoints != null) {
				const normalizedExpected = `${expectedPoints}`.replace(/^\+/, '');
				const normalizedOutcome = `${outcomePoints}`.replace(/^\+/, '');
				if (normalizedExpected !== normalizedOutcome) {
					return null;
				}
			}
			
			// Determine the expected sign based on expectedPoints
			const isNegative = expectedPoints != null && expectedPoints.startsWith('-');
			const isPositive = expectedPoints != null && !expectedPoints.startsWith('-') && expectedPoints !== '0';
			
			// Prioritize fields that match the expected sign
			// If expectedPoints is negative, prefer _negative fields
			// If expectedPoints is positive, prefer _positive fields
			// Otherwise, check all fields in order
			let prioritizedFields = [];
			if (isNegative) {
				// For negative points, prioritize negative fields first
				prioritizedFields = [
					'bet_price_history_over_negative',
					'bet_price_history_under_negative',
					'bet_price_over_history',
					'bet_price_under_history',
					'bet_price_history_over_positive',
					'bet_price_history_under_positive'
				];
			} else if (isPositive) {
				// For positive points, prioritize positive fields first
				prioritizedFields = [
					'bet_price_history_over_positive',
					'bet_price_history_under_positive',
					'bet_price_over_history',
					'bet_price_under_history',
					'bet_price_history_over_negative',
					'bet_price_history_under_negative'
				];
			} else {
				// If sign is unknown, check all fields in original order
				prioritizedFields = [
					'bet_price_over_history',
					'bet_price_under_history',
					'bet_price_history_over_positive',
					'bet_price_history_over_negative',
					'bet_price_history_under_positive',
					'bet_price_history_under_negative'
				];
			}
			
			// Check prioritized fields and return the first one that has data
			for (const field of prioritizedFields) {
				const data = oddsHistory[field];
				if (data && Array.isArray(data) && data.length > 0) {
					return data;
				}
			}
			
			return null;
		};

		// For under side with spreads, check all processed histories to ensure we find the correct team's data
		// The team filtering might be too strict, so we check all entries and filter in the lookup itself
		const historiesToCheck = selectedSide === 'under' && split && (split[1] === 'Point Spread' || split[1] === 'Asian Handicap')
			? finalProcessedOddsHistories
			: finalOddsHistories;
		
		// DEBUG: Log all entries being checked for under side
		if (selectedSide === 'under' && isSpreadMarket) {
			console.log('[UNDER DEBUG] All entries being checked:', historiesToCheck.map(oh => ({
				outcome_id_normalized: oh.outcome_id_normalized,
				sportsbook: oh.sportsbook,
				hasData: {
					bet_price_over_history: !!(oh.bet_price_over_history?.length),
					bet_price_under_history: !!(oh.bet_price_under_history?.length),
					bet_price_history_over_positive: !!(oh.bet_price_history_over_positive?.length),
					bet_price_history_over_negative: !!(oh.bet_price_history_over_negative?.length),
					bet_price_history_under_positive: !!(oh.bet_price_history_under_positive?.length),
					bet_price_history_under_negative: !!(oh.bet_price_history_under_negative?.length),
				}
			})));
		}
		
		// iterate through each odds history
		for (const oddsHistory of historiesToCheck) {
			
			
			// Include all entries (we've already filtered out individual WagerWiz Props/Mains)
			if (true) {
				// add new array into the xaxisvalues array for each odds history (with the minutes since)
				// add new array into the yaxisvalues array for each odds history (with the price)
				const xAxisValuesOdd = [];
				const yAxisValuesOdd = [];
				const xAxisValuesOddUnder = [];
				const yAxisValuesOddUnder = [];
				try {
					let overHistorySide = '';
					if (overSide === 'bet_price_over') {
						overHistorySide = 'bet_price_over_history';
					} else if (overSide === 'bet_price_over_positive') {
						overHistorySide = 'bet_price_history_over_positive';
					} else if (overSide === 'bet_price_over_negative') {
						overHistorySide = 'bet_price_history_over_negative';
					}

					
					// For Point Spread markets, use outcome_id_normalized to find the correct data
					// The backend may have data in the wrong field, so we check all fields
					let overData = null;
					if (split && (split[1] === 'Point Spread' || split[1] === 'Asian Handicap') && oddsHistory.outcome_id_normalized) {
						const parts = oddsHistory.outcome_id_normalized.split(':');
						if (parts.length >= 4) {
							const outcomeTeam = parts[2];
							const outcomePoints = parts[3];
							
							// For over side, we want the home team (which is split[2] from the main outcome)
							// split[2] is always the home team/over team regardless of selectedSide
							const overTeamFromSplit = normalizeTeamName(split[2]);
							const outcomeTeamNormalized = normalizeTeamName(outcomeTeam);
							
							// Check if this outcome is for the over team
							if (outcomeTeamNormalized === overTeamFromSplit) {
								// Determine expected points sign based on overSide
								// If overSide is bet_price_over_positive, points should be positive (no '-' sign)
								// If overSide is bet_price_over_negative, points should be negative (has '-' sign)
								const isPositive = overSide === 'bet_price_over_positive';
								const isNegative = overSide === 'bet_price_over_negative';
								
								// Check if the outcome points sign matches the expected sign
								const outcomePointsStartsWithMinus = outcomePoints.startsWith('-');
								const outcomePointsIsPositive = !outcomePointsStartsWithMinus && outcomePoints !== '0';
								
								// Determine if this entry's points sign matches what we expect
								let pointsSignMatches = false;
								if (points != null) {
									// If points is available, check exact match
									const rawPoints = `${points}`;
									const expectedPoints = (isPositive ? rawPoints : (isNegative ? `-${rawPoints}` : null));
									const normalizedExpected = expectedPoints != null ? expectedPoints.replace(/^\+/, '') : null;
									const normalizedOutcome = outcomePoints.replace(/^\+/, '');
									pointsSignMatches = normalizedExpected === normalizedOutcome;
								} else {
									// If points is undefined, just check the sign matches
									// For bet_price_over_negative, we want negative points
									// For bet_price_over_positive, we want positive points
									if (isNegative) {
										pointsSignMatches = outcomePointsStartsWithMinus;
									} else if (isPositive) {
										pointsSignMatches = outcomePointsIsPositive;
									}
								}
								
								// If points sign matches, this is the correct outcome
								if (pointsSignMatches) {
									// Determine expectedPoints for findHistoryDataByOutcomeId
									// Always construct expectedPoints with the correct sign to ensure findHistoryDataByOutcomeId validates it
									let expectedPoints = null;
									if (points != null) {
										const rawPoints = `${points}`;
										expectedPoints = (isPositive ? rawPoints : (isNegative ? `-${rawPoints}` : null));
									} else {
										// When points is undefined, we still need to construct expectedPoints with the correct sign
										// Extract the raw points value from outcomePoints and apply the sign we expect
										const rawPointsFromOutcome = outcomePoints.replace(/^[-+]/, '');
										expectedPoints = (isPositive ? rawPointsFromOutcome : (isNegative ? `-${rawPointsFromOutcome}` : null));
									}
									
									// Find data in any field for this team and points combination
									// findHistoryDataByOutcomeId will verify both team and points match
									overData = findHistoryDataByOutcomeId(oddsHistory, outcomeTeam, expectedPoints);
								}
							}
						}
					}
					
					// Fallback to original logic if not a spread or if outcome-based lookup failed
					if (!overData) {
						overData = oddsHistory[overHistorySide];
						if (!overData || !Array.isArray(overData) || overData.length === 0) {
							// Try alternative over-side fields
							if (overHistorySide === 'bet_price_history_over_positive') {
								overData = oddsHistory['bet_price_history_over_negative'] || oddsHistory['bet_price_over_history'];
							} else if (overHistorySide === 'bet_price_history_over_negative') {
								overData = oddsHistory['bet_price_history_over_positive'] || oddsHistory['bet_price_over_history'];
							} else if (overHistorySide === 'bet_price_over_history') {
								// Try positive/negative variants if main over_history is empty
								overData = oddsHistory['bet_price_history_over_positive'] || oddsHistory['bet_price_history_over_negative'];
							}
						}
					}
					if (overData && Array.isArray(overData)) {
						for (const betPrice of overData) {
							if (betPrice.time || betPrice.timestamp) {
								const dateObj = new Date(betPrice.time || betPrice.timestamp);
								if (!isNaN(dateObj.getTime())) {
									if (timeRange === '15m') {
										const minutes = minutesSince(dateObj);
										if (minutes < 15) {
											xAxisValuesOdd.push(minutesSince(dateObj));
											yAxisValuesOdd.push({ price: betPrice.price, book: oddsHistory.sportsbook, colour: oddsHistory.colour, originalSportsbook: oddsHistory.originalSportsbook || oddsHistory.sportsbook });
										}
									} else if (timeRange === '1h') {
										const minutes = minutesSince(dateObj);
										if (minutes < 60) {
											xAxisValuesOdd.push(minutesSince(dateObj));
											yAxisValuesOdd.push({ price: betPrice.price, book: oddsHistory.sportsbook, colour: oddsHistory.colour, originalSportsbook: oddsHistory.originalSportsbook || oddsHistory.sportsbook });
										}
									} else if (timeRange === '24h') {
										const minutes = minutesSince(dateObj);
										if (minutes < 1440) {
											xAxisValuesOdd.push(minutesSince(dateObj));
											yAxisValuesOdd.push({ price: betPrice.price, book: oddsHistory.sportsbook, colour: oddsHistory.colour, originalSportsbook: oddsHistory.originalSportsbook || oddsHistory.sportsbook });
										}
									} else {
										xAxisValuesOdd.push(minutesSince(dateObj));
										yAxisValuesOdd.push({ price: betPrice.price, book: oddsHistory.sportsbook, colour: oddsHistory.colour });
									}
								}
							}
						}
					} // Close the safety check if statement
				} catch (e) {
					// console.log('e: ', e);
				}
				try {
					let underHistorySide = '';
					if (underSide === 'bet_price_under') {
						underHistorySide = 'bet_price_under_history';
					} else if (underSide === 'bet_price_under_positive') {
						underHistorySide = 'bet_price_history_under_positive';
					} else if (underSide === 'bet_price_under_negative') {
						underHistorySide = 'bet_price_history_under_negative';
					}

					
					// For Point Spread markets, use outcome_id_normalized to find the correct data
					// The backend may have data in the wrong field, so we check all fields
					let underData = null;
					if (split && (split[1] === 'Point Spread' || split[1] === 'Asian Handicap') && oddsHistory.outcome_id_normalized) {
						const parts = oddsHistory.outcome_id_normalized.split(':');
						if (parts.length >= 4) {
							const outcomeTeam = parts[2];
							const outcomePoints = parts[3];
							
							// For under side, we want the team that matches the targetTeam
							// targetTeamForLookup is already calculated correctly:
							// - For under side: it's the other team (not in split[2])
							// - For over side: it's split[2]
							const outcomeTeamNormalized = normalizeTeamName(outcomeTeam);
							
							// Check if this outcome is for the target team
							if (targetTeamForLookup && outcomeTeamNormalized === targetTeamForLookup) {
								// Determine expected points sign based on underSide
								// If underSide is bet_price_under_positive, points should be positive (no '-' sign)
								// If underSide is bet_price_under_negative, points should be negative (has '-' sign)
								const isPositive = underSide === 'bet_price_under_positive';
								const isNegative = underSide === 'bet_price_under_negative';
								
								// Check if the outcome points sign matches the expected sign
								const outcomePointsStartsWithMinus = outcomePoints.startsWith('-');
								const outcomePointsIsPositive = !outcomePointsStartsWithMinus && outcomePoints !== '0';
								
								// Determine if this entry's points sign matches what we expect
								let pointsSignMatches = false;
								if (points != null) {
									// If points is available, check exact match
									const rawPoints = `${points}`;
									const expectedPoints = (isPositive ? rawPoints : (isNegative ? `-${rawPoints}` : null));
									const normalizedExpected = expectedPoints != null ? expectedPoints.replace(/^\+/, '') : null;
									const normalizedOutcome = outcomePoints.replace(/^\+/, '');
									pointsSignMatches = normalizedExpected === normalizedOutcome;
								} else {
									// If points is undefined, just check the sign matches
									// For bet_price_under_negative, we want negative points
									// For bet_price_under_positive, we want positive points
									if (isNegative) {
										pointsSignMatches = outcomePointsStartsWithMinus;
									} else if (isPositive) {
										pointsSignMatches = outcomePointsIsPositive;
									}
								}
								
								// DEBUG: Log under side lookup
								if (selectedSide === 'under') {
									console.log('[UNDER DEBUG] Checking entry:', {
										outcome_id_normalized: oddsHistory.outcome_id_normalized,
										outcomeTeam,
										outcomePoints,
										underSide,
										points,
										isPositive,
										isNegative,
										outcomePointsStartsWithMinus,
										outcomePointsIsPositive,
										pointsSignMatches,
										targetTeamForLookup,
										outcomeTeamNormalized,
										teamMatch: true
									});
								}
								
								// If points sign matches, this is the correct outcome
								if (pointsSignMatches) {
									// Determine expectedPoints for findHistoryDataByOutcomeId
									// Always construct expectedPoints with the correct sign to ensure findHistoryDataByOutcomeId validates it
									let expectedPoints = null;
									if (points != null) {
										const rawPoints = `${points}`;
										expectedPoints = (isPositive ? rawPoints : (isNegative ? `-${rawPoints}` : null));
									} else {
										// When points is undefined, we still need to construct expectedPoints with the correct sign
										// Extract the raw points value from outcomePoints and apply the sign we expect
										const rawPointsFromOutcome = outcomePoints.replace(/^[-+]/, '');
										expectedPoints = (isPositive ? rawPointsFromOutcome : (isNegative ? `-${rawPointsFromOutcome}` : null));
									}
									
									// CRITICAL: Verify that this entry's outcome_id_normalized exactly matches what we expect
									// Double-check the points match exactly (this should already be verified by pointsSignMatches, but be extra sure)
									const normalizedExpectedPoints = expectedPoints != null ? expectedPoints.replace(/^\+/, '') : null;
									const normalizedOutcomePoints = outcomePoints.replace(/^\+/, '');
									const pointsMatchExactly = normalizedExpectedPoints === normalizedOutcomePoints;
									
									if (pointsMatchExactly) {
										// Find data in any field for this team and points combination
										// findHistoryDataByOutcomeId will verify both team and points match
										const foundData = findHistoryDataByOutcomeId(oddsHistory, outcomeTeam, expectedPoints);
										
										// Only use this data if we haven't found data yet for this sportsbook
										// (We want to ensure we use the exact outcome_id_normalized match)
										// CRITICAL: Only set underData if we haven't set it yet
										// This prevents overwriting correct data with wrong data from a different entry
										if (foundData && Array.isArray(foundData) && foundData.length > 0) {
											// Check if we already have data for this sportsbook
											const hadExistingData = !!underData;
											
											// Only use this data if we don't have data yet
											// This ensures we use the first matching entry and don't overwrite it
											if (!hadExistingData) {
												underData = foundData;
												
												// DEBUG: Log if data was found
												if (selectedSide === 'under') {
													// Determine which field the data came from
													let dataSource = 'unknown';
													for (const field of ['bet_price_over_history', 'bet_price_under_history', 'bet_price_history_over_positive', 'bet_price_history_over_negative', 'bet_price_history_under_positive', 'bet_price_history_under_negative']) {
														if (oddsHistory[field] === foundData) {
															dataSource = field;
															break;
														}
													}
													
													console.log('[UNDER DEBUG] Data found for correct outcome:', {
														outcome_id_normalized: oddsHistory.outcome_id_normalized,
														sportsbook: oddsHistory.sportsbook,
														expectedPoints,
														outcomePoints,
														normalizedExpectedPoints,
														normalizedOutcomePoints,
														pointsMatchExactly,
														dataLength: foundData.length,
														firstPrice: foundData[0]?.price,
														firstAmericanPrice: foundData[0]?.american_price,
														dataSource,
														hadExistingData
													});
												}
											} else if (selectedSide === 'under') {
												// DEBUG: Log if we're skipping this data because we already have data
												console.log('[UNDER DEBUG] Skipping data - already have data for this sportsbook:', {
													outcome_id_normalized: oddsHistory.outcome_id_normalized,
													sportsbook: oddsHistory.sportsbook,
													hadExistingData
												});
											}
										} else if (selectedSide === 'under') {
											// DEBUG: Log if data was not found even though sign matched
											console.log('[UNDER DEBUG] Sign matched but no data found:', {
												outcome_id_normalized: oddsHistory.outcome_id_normalized,
												sportsbook: oddsHistory.sportsbook,
												expectedPoints,
												outcomePoints
											});
										}
									} else if (selectedSide === 'under') {
										// DEBUG: Log if points don't match exactly
										console.log('[UNDER DEBUG] Points do not match exactly:', {
											outcome_id_normalized: oddsHistory.outcome_id_normalized,
											sportsbook: oddsHistory.sportsbook,
											expectedPoints,
											outcomePoints,
											normalizedExpectedPoints,
											normalizedOutcomePoints,
											pointsMatchExactly
										});
									}
								}
							}
						}
					}
					
					// Fallback to original logic if not a spread or if outcome-based lookup failed
					// BUT: For spread markets, only use fallback if we've verified the outcome_id_normalized matches
					if (!underData) {
						// For spread markets, don't use fallback unless we've verified the entry matches
						// This prevents using data from the wrong outcome_id_normalized
						if (split && (split[1] === 'Point Spread' || split[1] === 'Asian Handicap') && oddsHistory.outcome_id_normalized) {
							// We already tried to find data above, but it didn't match
							// Don't use fallback for spread markets to avoid wrong data
							underData = null;
						} else {
							// For non-spread markets, use the original fallback logic
							underData = oddsHistory[underHistorySide];
							if (!underData || !Array.isArray(underData) || underData.length === 0) {
								// Try alternative under-side fields
								if (underHistorySide === 'bet_price_history_under_positive') {
									underData = oddsHistory['bet_price_history_under_negative'] || oddsHistory['bet_price_under_history'];
								} else if (underHistorySide === 'bet_price_history_under_negative') {
									underData = oddsHistory['bet_price_history_under_positive'] || oddsHistory['bet_price_under_history'];
								} else if (underHistorySide === 'bet_price_under_history') {
									// Try positive/negative variants if main under_history is empty
									underData = oddsHistory['bet_price_history_under_positive'] || oddsHistory['bet_price_history_under_negative'];
								}
							}
						}
					}
					// DEBUG: Log what data we're about to process for under side
					if (selectedSide === 'under' && underData && Array.isArray(underData)) {
						console.log('[UNDER DEBUG] Processing underData for sportsbook:', {
							sportsbook: oddsHistory.sportsbook,
							outcome_id_normalized: oddsHistory.outcome_id_normalized,
							dataLength: underData.length,
							firstFewPrices: underData.slice(0, 3).map(d => d.price || d.american_price),
							firstFewAmericanPrices: underData.slice(0, 3).map(d => d.american_price)
						});
					}
					
					if (underData && Array.isArray(underData)) {
						for (const betPrice of underData) {
							if (betPrice.time || betPrice.timestamp) {
								const dateObj = new Date(betPrice.time || betPrice.timestamp);
								if (!isNaN(dateObj.getTime())) {
									if (timeRange === '15m') {
										const minutes = minutesSince(dateObj);
										if (minutes < 15) {
											xAxisValuesOddUnder.push(minutesSince(dateObj));
											yAxisValuesOddUnder.push({ price: betPrice.price, book: oddsHistory.sportsbook, colour: oddsHistory.colour, originalSportsbook: oddsHistory.originalSportsbook || oddsHistory.sportsbook });
										}
									} else if (timeRange === '1h') {
										const minutes = minutesSince(dateObj);
										if (minutes < 60) {
											xAxisValuesOddUnder.push(minutesSince(dateObj));
											yAxisValuesOddUnder.push({ price: betPrice.price, book: oddsHistory.sportsbook, colour: oddsHistory.colour, originalSportsbook: oddsHistory.originalSportsbook || oddsHistory.sportsbook });
										}
									} else if (timeRange === '24h') {
										const minutes = minutesSince(dateObj);
										if (minutes < 1440) {
											xAxisValuesOddUnder.push(minutesSince(dateObj));
											yAxisValuesOddUnder.push({ price: betPrice.price, book: oddsHistory.sportsbook, colour: oddsHistory.colour, originalSportsbook: oddsHistory.originalSportsbook || oddsHistory.sportsbook });
										}
									} else {
										xAxisValuesOddUnder.push(minutesSince(dateObj));
										yAxisValuesOddUnder.push({ price: betPrice.price, book: oddsHistory.sportsbook, colour: oddsHistory.colour });
									}
								}
							}
						}
					} // Close the under side safety check if statement
				} catch (e) {}

				
				// Apply smoothing to WagerWiz data to reduce spikiness
				const isWagerWiz = oddsHistory.sportsbook === 'WagerWiz' || 
					oddsHistory.originalSportsbook === 'WagerWiz' ||
					(oddsHistory.sportsbook && oddsHistory.sportsbook.includes('WagerWiz'));
				
				let finalXAxisValuesOdd = xAxisValuesOdd;
				let finalYAxisValuesOdd = yAxisValuesOdd;
				let finalXAxisValuesOddUnder = xAxisValuesOddUnder;
				let finalYAxisValuesOddUnder = yAxisValuesOddUnder;
				
				if (isWagerWiz) {
					// Smooth the over side data using a 15-minute time window
					// This handles disconnected data points from different sportsbooks with different timestamps
					if (xAxisValuesOdd.length > 0 && yAxisValuesOdd.length > 0) {
						const smoothedOver = smoothPriceData(xAxisValuesOdd, yAxisValuesOdd, 15);
						finalXAxisValuesOdd = smoothedOver.xValues;
						finalYAxisValuesOdd = smoothedOver.yValues;
					}
					
					// Smooth the under side data using a 15-minute time window
					if (xAxisValuesOddUnder.length > 0 && yAxisValuesOddUnder.length > 0) {
						const smoothedUnder = smoothPriceData(xAxisValuesOddUnder, yAxisValuesOddUnder, 15);
						finalXAxisValuesOddUnder = smoothedUnder.xValues;
						finalYAxisValuesOddUnder = smoothedUnder.yValues;
					}
				}
				
				console.log('OddsHistoryGraph: Adding data for', oddsHistory.sportsbook, '- over:', finalXAxisValuesOdd.length, 'points, under:', finalXAxisValuesOddUnder.length, 'points');
				
				// DEBUG: Log under side data collection
				if (selectedSide === 'under' && finalXAxisValuesOddUnder.length > 0) {
					console.log('[UNDER DEBUG] Collected under data for', oddsHistory.sportsbook, ':', finalXAxisValuesOddUnder.length, 'points');
				}
				
				xAxisValues.push(finalXAxisValuesOdd);
				yAxisValues.push(finalYAxisValuesOdd);
				// derive team label from outcome_id_normalized for spreads
				try {
					let labelTeam = '';
					const parts = (oddsHistory.outcome_id_normalized || '').split(':');
					if (parts[1] === 'Point Spread' || parts[1] === 'Asian Handicap') {
						labelTeam = parts[2] || '';
					}
					seriesTeamLabels.push(labelTeam);
					seriesTeamLabelsUnder.push(labelTeam);
				} catch (e) { seriesTeamLabels.push(''); seriesTeamLabelsUnder.push(''); }
				xAxisValuesUnder.push(finalXAxisValuesOddUnder);
				yAxisValuesUnder.push(finalYAxisValuesOddUnder);
			}
		}
	}

	console.log('OddsHistoryGraph: Final arrays - xAxisValues:', xAxisValues.length, 'yAxisValues:', yAxisValues.length, 'xAxisValuesUnder:', xAxisValuesUnder.length, 'yAxisValuesUnder:', yAxisValuesUnder.length);
	
	// DEBUG: Summary for under side
	if (selectedSide === 'under') {
		console.log('[UNDER DEBUG] Final summary:', {
			selectedSide,
			underSide,
			points,
			xAxisValuesUnderLength: xAxisValuesUnder.length,
			yAxisValuesUnderLength: yAxisValuesUnder.length,
			xAxisValuesUnderDetails: xAxisValuesUnder.map((arr, i) => ({ index: i, length: arr.length })),
			yAxisValuesUnderDetails: yAxisValuesUnder.map((arr, i) => ({ index: i, length: arr.length, firstPrice: arr[0]?.price }))
		});
	}
	
	// Get the longest x-axis array to use as the base (ensures data length matches)
	const getLongestXAxis = (xAxisArrays) => {
		if (!xAxisArrays || xAxisArrays.length === 0) {
			return [0];
		}
		// Sort by length and get the longest one
		const sorted = [...xAxisArrays].sort((a, b) => (b?.length || 0) - (a?.length || 0));
		const longest = sorted[0];
		if (!longest || !Array.isArray(longest) || longest.length === 0) {
			return [0];
		}
		// Filter valid values, add 0, sort and reverse
		const validData = longest.filter(val => val != null && !isNaN(val) && isFinite(val));
		if (validData.length === 0) {
			return [0];
		}
		validData.push(0);
		const sortedData = [...validData].sort((a, b) => b - a);
		return sortedData.reverse();
	};
	
	const baseXAxis = getLongestXAxis(xAxisValues);
	const baseXAxisUnder = getLongestXAxis(xAxisValuesUnder);
	
	const xAxes = [{
		data: baseXAxis,
		label: 'Time',
		valueFormatter: (value, context) => {
			if (value == null || isNaN(value) || !isFinite(value)) return '';
			const minutesAgo = Math.round(value);
			if (minutesAgo < 0 || minutesAgo > 100000) return '';
			const now = new Date();
			const timeAtPoint = new Date(now.getTime() - minutesAgo * 60 * 1000);
			if (isNaN(timeAtPoint.getTime())) return '';
			const hours = timeAtPoint.getHours();
			const minutes = timeAtPoint.getMinutes();
			const ampm = hours >= 12 ? 'PM' : 'AM';
			const displayHours = hours % 12 || 12;
			const displayMinutes = minutes.toString().padStart(2, '0');
			return `${displayHours}:${displayMinutes} ${ampm}`;
		},
		reverse: true,
		sx: { '.MuiChartsAxis-tickLabel': { fill: '#b1b6c3' }, '.MuiChartsAxis-label': { fill: '#b1b6c3' } },
	}];

	// Get the target length from the base x-axis
	const targetLength = baseXAxis.length;
	
	const yAxes = yAxisValues
		?.sort((a, b) => b.length - a.length)
		.map((e, i) => {
			try {
				const data = e?.map((t) => t.price);
				let book = getSportsbookDisplayName(e?.[0].book === 'WagerWiz Mains, WagerWiz Props' ? 'WagerWiz' : e?.[0].book);
				// Use originalSportsbook for color lookup to ensure consistency
				const originalSportsbook = e?.[0].originalSportsbook || e?.[0].book;
				let colour = e?.[0].colour;
				// Re-verify color based on originalSportsbook to ensure it matches
				if (originalSportsbook === 'WagerWiz') {
					colour = '#0042a4';
				} else if (originalSportsbook && sportsbookColourMapping[originalSportsbook]) {
					colour = sportsbookColourMapping[originalSportsbook];
				} else if (!colour) {
					colour = getRandomColor();
				}
				data.push(data[data.length - 1]);
				
				// Pad or trim data to match x-axis length
				const reversedData = [...data].reverse();
				if (reversedData.length < targetLength) {
					// Pad with last value
					const lastValue = reversedData[reversedData.length - 1];
					while (reversedData.length < targetLength) {
						reversedData.push(lastValue);
					}
				} else if (reversedData.length > targetLength) {
					// Trim to target length
					reversedData.splice(targetLength);
				}

				return {
					data: reversedData,
					color: colour,
					label: book,
					valueFormatter: (value) => {
						const americanValue = convertDecimalToAmerican(value);
						if (americanValue !== null) {
							const formatted = americanValue.toFixed(0);
							return americanValue > 0 ? `+${formatted}` : formatted;
						}
						return null;
					},
					curve: 'linear',
					showMark: false,
					reverse: true,
				};
			} catch (e) {
				return {
					data: [].reverse(),
					color: '#FFFFF',
					label: ``,
					valueFormatter: (value) => {
						const americanValue = convertDecimalToAmerican(value);
						if (americanValue !== null) {
							const formatted = americanValue.toFixed(0);
							return americanValue > 0 ? `+${formatted}` : formatted;
						}
						return null;
					},
					curve: 'linear',
					showMark: false,
					reverse: true,
				};
			}
		});

	const xAxesUnder = [{
		data: baseXAxisUnder,
		label: 'Time',
		valueFormatter: (value, context) => {
			if (value == null || isNaN(value) || !isFinite(value)) return '';
			const minutesAgo = Math.round(value);
			if (minutesAgo < 0 || minutesAgo > 100000) return '';
			const now = new Date();
			const timeAtPoint = new Date(now.getTime() - minutesAgo * 60 * 1000);
			if (isNaN(timeAtPoint.getTime())) return '';
			const hours = timeAtPoint.getHours();
			const minutes = timeAtPoint.getMinutes();
			const ampm = hours >= 12 ? 'PM' : 'AM';
			const displayHours = hours % 12 || 12;
			const displayMinutes = minutes.toString().padStart(2, '0');
			return `${displayHours}:${displayMinutes} ${ampm}`;
		},
		reverse: true,
		sx: { '.MuiChartsAxis-tickLabel': { fill: '#b1b6c3' }, '.MuiChartsAxis-label': { fill: '#b1b6c3' } },
	}];

	// Get the target length from the base x-axis for under side
	const targetLengthUnder = baseXAxisUnder.length;
	
	const yAxesUnder = yAxisValuesUnder
		?.sort((a, b) => b.length - a.length)
		.map((e, i) => {
			try {
				const data = e?.map((t) => t.price);
				let book = getSportsbookDisplayName(e?.[0].book === 'WagerWiz Mains, WagerWiz Props' ? 'WagerWiz' : e?.[0].book);
				// Use originalSportsbook for color lookup to ensure consistency
				const originalSportsbook = e?.[0].originalSportsbook || e?.[0].book;
				let colour = e?.[0].colour;
				// Re-verify color based on originalSportsbook to ensure it matches
				if (originalSportsbook === 'WagerWiz') {
					colour = '#0042a4';
				} else if (originalSportsbook && sportsbookColourMapping[originalSportsbook]) {
					colour = sportsbookColourMapping[originalSportsbook];
				} else if (!colour) {
					colour = getRandomColor();
				}

				data.push(data[data.length - 1]);
				
				// Pad or trim data to match x-axis length
				const reversedData = [...data].reverse();
				if (reversedData.length < targetLengthUnder) {
					// Pad with last value
					const lastValue = reversedData[reversedData.length - 1];
					while (reversedData.length < targetLengthUnder) {
						reversedData.push(lastValue);
					}
				} else if (reversedData.length > targetLengthUnder) {
					// Trim to target length
					reversedData.splice(targetLengthUnder);
				}

				return {
					data: reversedData,
					color: colour,
					label: book,
					valueFormatter: (value) => {
						const americanValue = convertDecimalToAmerican(value);
						if (americanValue !== null) {
							const formatted = americanValue.toFixed(0);
							return americanValue > 0 ? `+${formatted}` : formatted;
						}
						return null;
					},
					curve: 'linear',
					showMark: false,
					reverse: true,
				};
			} catch (e) {
				return {
					data: [].reverse(),
					color: '#FFFFF',
					label: ``,
					valueFormatter: (value) => {
						const americanValue = convertDecimalToAmerican(value);
						if (americanValue !== null) {
							const formatted = americanValue.toFixed(0);
							return americanValue > 0 ? `+${formatted}` : formatted;
						}
						return null;
					},
					curve: 'linear',
					showMark: false,
					reverse: true,
				};
			}
		});

	const yaxesvalues = yAxes.map((line) => line.data);
	const yaxesvaluesunder = yAxesUnder.map((line) => line.data);
	

	const tickValues = generateTickValues(yaxesvalues);
	const tickValuesUnder = generateTickValues(yaxesvaluesunder);

	return (
		<div>
			{typeof setIsModalOpen === 'function' && (
				<button
					onClick={() => {
						setIsModalOpen(!isModalOpen);
					}} // your close logic here
					className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors">
					{/* <Close /> */}
					{isMobile && (
						<div style={{ marginTop: 12 }}>
							{sportsbooksToHighlight?.map((book) => {
								let sportsbook = book;
								// if (sharpBook?.includes(book) && (sharpFilter.includes('WagerWiz Props') || sharpFilter.includes('WagerWiz Mains') || sharpFilter.includes('WagerWiz'))) {
								// 	sportsbook = 'WagerWiz';
								// }
								return <LogoGenerator isMobile sportsbook={sportsbook} />;
							})}
						</div>
					)}
				</button>
			)}
			<div className={`${!isMobile && 'pt-6 pl-4'}`}>
				<div className={`flex flex-row ${!isMobile && 'pl-6'}`}></div>
				{isMobile && (
					<div
						className={`${!isMobile && 'pl-4'}`}
						style={{ backgroundColor: !isMobile ? '#363844' : '', height: '56px', alignContent: 'center', color: '#eff1f5', maxWidth: 300 }}>
						{split[2]} {selectedSide} {split[3]} {isMobile && split[1]}
					</div>
				)}
				{isMobile && (
					<div style={{ marginLeft: 'auto', marginRight: 70, transform: 'scale(0.8)' }}>
						<TimeRangeSelector selected={selected} setSelected={setSelected} isMobile />
					</div>
				)}
				{selectedSide === 'over' ? (
					<>
						<LineChart
							xAxis={xAxes}
							series={yAxes}
							axisHighlight={{ x: 'line', y: 'none' }}
							tooltip={{ trigger: 'axis' }}
							margin={{ left: 75, right: 50, top: 20, bottom: 50 }}
							yAxis={[
								{
									label: 'Price',
									valueFormatter: (value) => {
										const americanValue = convertDecimalToAmerican(value);
										if (americanValue !== null && americanValue > -10000 && americanValue < 10000) {
											const formatted = americanValue.toFixed(0);
											return americanValue > 0 ? `+${formatted}` : formatted;
										} else return 0;
									},
									sx: { 
										'.MuiChartsAxis-tickLabel': { 
											fill: '#b1b6c3',
											marginLeft: '0px',
										}, 
										'.MuiChartsAxis-label': { 
											fill: '#b1b6c3',
											marginRight: '0px',
										},
									},
									tickInterval: tickValues,
									min: Math.min(...tickValues),
									max: Math.max(...tickValues),
								},
							]}
							width={!isMobile ? pageWidth * 0.7 : pageWidth * 0.9}
							height={height}
							slotProps={{
								legend: { hidden: true },
								popper: {
									sx: {
										'& .MuiChartsTooltip-paper': {
											backgroundColor: '#1a1d24 !important',
											border: '1px solid #363844 !important',
											borderRadius: '8px !important',
											padding: '10px 14px !important',
											boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5) !important',
										},
										'& .MuiChartsTooltip-title': {
											color: '#b1b6c3 !important',
											fontSize: '11px !important',
											fontWeight: '500 !important',
											marginBottom: '8px !important',
											paddingBottom: '8px !important',
											borderBottom: '1px solid #363844 !important',
										},
										'& .MuiChartsTooltip-mark': {
											width: '10px !important',
											height: '10px !important',
											borderRadius: '50% !important',
										},
										'& .MuiChartsTooltip-label': {
											color: '#eff1f5 !important',
											fontSize: '13px !important',
											fontWeight: '500 !important',
											padding: '4px 10px !important',
										},
										'& .MuiChartsTooltip-value': {
											color: '#eff1f5 !important',
											fontSize: '13px !important',
											fontWeight: '600 !important',
											padding: '4px 10px !important',
										},
										'& .MuiTypography-root': {
											color: '#eff1f5 !important',
										},
									},
								},
							}}
							skipAnimation={true}
							grid={{ horizontal: false }}
							sx={{
								'& .MuiChartsAxis-label': {
									color: '#b1b6c3',
								},
								'& .MuiChartsAxis-directionX': {
									paddingLeft: '10px !important',
									paddingRight: '10px !important',
								},
								'& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': {
									textAnchor: 'middle !important',
									maxWidth: '85px !important',
									overflow: 'visible !important',
								},
								'& .MuiChartsAxis-directionY .MuiChartsAxis-label': {
									transform: 'translateX(-25px) !important',
								},
								'& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
									transform: 'translateX(0px) !important',
									textAnchor: 'end !important',
									marginRight: '8px !important',
								},
								'&. MuiChartsGrid-line': {
									stroke: 'white',
								},
							}}
						/>
					</>
				) : (
					<>
						<LineChart
							xAxis={xAxesUnder}
							series={yAxesUnder}
							axisHighlight={{ x: 'line', y: 'none' }}
							tooltip={{ trigger: 'axis' }}
							margin={{ left: 75, right: 50, top: 20, bottom: 50 }}
							skipAnimation={true}
							yAxis={[
								{
									label: 'Price',
									valueFormatter: (value) => {
										const americanValue = convertDecimalToAmerican(value);
										if (americanValue !== null && americanValue > -10000 && americanValue < 10000) {
											const formatted = americanValue.toFixed(0);
											return americanValue > 0 ? `+${formatted}` : formatted;
										} else return 0;
									},
									sx: { 
										'.MuiChartsAxis-tickLabel': { 
											fill: '#b1b6c3',
											marginLeft: '0px',
										}, 
										'.MuiChartsAxis-label': { 
											fill: '#b1b6c3',
											marginRight: '0px',
										},
									},
									tickInterval: tickValuesUnder,
									min: Math.min(...tickValuesUnder),
									max: Math.max(...tickValuesUnder),
								},
							]}
							width={!isMobile ? pageWidth * 0.7 : pageWidth * 0.9}
							height={height}
							slotProps={{
								legend: { hidden: true },
								popper: {
									sx: {
										'& .MuiChartsTooltip-paper': {
											backgroundColor: '#1a1d24 !important',
											border: '1px solid #363844 !important',
											borderRadius: '8px !important',
											padding: '10px 14px !important',
											boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5) !important',
										},
										'& .MuiChartsTooltip-title': {
											color: '#b1b6c3 !important',
											fontSize: '11px !important',
											fontWeight: '500 !important',
											marginBottom: '8px !important',
											paddingBottom: '8px !important',
											borderBottom: '1px solid #363844 !important',
										},
										'& .MuiChartsTooltip-mark': {
											width: '10px !important',
											height: '10px !important',
											borderRadius: '50% !important',
										},
										'& .MuiChartsTooltip-label': {
											color: '#eff1f5 !important',
											fontSize: '13px !important',
											fontWeight: '500 !important',
											padding: '4px 10px !important',
										},
										'& .MuiChartsTooltip-value': {
											color: '#eff1f5 !important',
											fontSize: '13px !important',
											fontWeight: '600 !important',
											padding: '4px 10px !important',
										},
										'& .MuiTypography-root': {
											color: '#eff1f5 !important',
										},
									},
								},
							}}
							grid={{ horizontal: false }}
							sx={{
								'& .MuiChartsAxis-label': {
									// padding: "10px !important",
									// fill: "white !important",
									marginRight: '20px',
								},
								'& .MuiChartsAxis-directionY .MuiChartsAxis-label': {
									transform: 'translateX(-8px) !important',
									marginRight: '32px !important',
								},
								'& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
									marginLeft: '24px !important',
								},
							}}
						/>
					</>
				)}
			</div>
		</div>
	);
};
